import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Search, CheckCircle, XCircle, Users, Loader, QrCode,
  ChevronLeft, AlertCircle, Camera, CameraOff, Store
} from "lucide-react";
import { getEventVendors, checkInVendor } from "../../services/VendorCheckInService";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebaseFunctions/config";
import { isDemoMode } from "../../demo/isDemoMode";
import demoStore from "../../demo/demoStore";
import jsQR from "jsqr";

const VendorCheckIn = () => {
  const { id: eventId } = useParams();
  const [eventName, setEventName] = useState("");
  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // "all" | "in" | "pending"
  const [qrInput, setQrInput] = useState("");
  const [activeTab, setActiveTab] = useState("list"); // "list" | "scanner"
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);

  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [lastScanned, setLastScanned] = useState(null);

  const qrInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  useEffect(() => {
    if (activeTab === "scanner" && !cameraActive && qrInputRef.current) {
      qrInputRef.current.focus();
    }
  }, [activeTab, cameraActive]);

  useEffect(() => {
    if (activeTab !== "scanner") stopCamera();
  }, [activeTab]);

  useEffect(() => {
    return () => stopCamera();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      let name = "";
      if (isDemoMode) {
        const eventData = demoStore.getDoc("events", eventId);
        name = eventData?.eventName || "";
      } else {
        const snap = await getDoc(doc(db, "events", eventId));
        if (snap.exists()) name = snap.data().eventName || "";
      }
      setEventName(name);

      const result = await getEventVendors(eventId);
      if (result.success) setVendors(result.vendors || []);
    } catch (err) {
      console.error("VendorCheckIn load error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckIn = async (rawData) => {
    if (scanning) return;
    setScanning(true);
    setScanResult(null);

    // Parse QR — expects JSON { applicationId, eventId, type: 'vendor-checkin' }
    let applicationId = rawData.trim();
    try {
      const parsed = JSON.parse(rawData);
      if (parsed.type !== "vendor-checkin") {
        setScanResult({ status: "error", message: "This QR code is not a vendor check-in pass" });
        setScanning(false);
        return;
      }
      if (parsed.eventId && parsed.eventId !== eventId) {
        setScanResult({ status: "error", message: "This pass is for a different event" });
        setScanning(false);
        return;
      }
      applicationId = parsed.applicationId;
    } catch {
      // Not JSON — treat raw value as applicationId (fallback for manual entry)
    }

    const result = await checkInVendor({ applicationId, eventId });

    if (result.alreadyCheckedIn) {
      setScanResult({
        status: "already",
        businessName: result.businessName,
        boothNumber: result.boothNumber,
        message: "Already checked in",
      });
    } else if (result.success) {
      setScanResult({
        status: "success",
        businessName: result.businessName,
        boothNumber: result.boothNumber,
      });
      setVendors(prev =>
        prev.map(v => v.id === applicationId ? { ...v, vendorCheckedIn: true } : v)
      );
    } else {
      setScanResult({ status: "error", message: result.error || "Invalid pass" });
    }

    setScanning(false);
    setQrInput("");
    if (qrInputRef.current && !cameraActive) qrInputRef.current.focus();
  };

  const handleQrSubmit = (e) => {
    e.preventDefault();
    if (!qrInput.trim()) return;
    handleCheckIn(qrInput);
  };

  // ── Camera ────────────────────────────────────────────────────────────────

  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setCameraActive(true);
      scanFrame();
    } catch {
      setCameraError("Camera access denied. Use the guest list or paste an ID manually.");
    }
  };

  const stopCamera = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const scanFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(scanFrame);
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height);

    if (code?.data && code.data !== lastScanned) {
      setLastScanned(code.data);
      handleCheckIn(code.data);
      setTimeout(() => setLastScanned(null), 3000);
    }

    rafRef.current = requestAnimationFrame(scanFrame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastScanned]);

  useEffect(() => {
    if (cameraActive) rafRef.current = requestAnimationFrame(scanFrame);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [cameraActive, scanFrame]);

  // ─────────────────────────────────────────────────────────────────────────

  const filtered = vendors.filter(v => {
    const matchesSearch = !search ||
      v.businessName?.toLowerCase().includes(search.toLowerCase()) ||
      v.boothNumber?.toString().includes(search);
    const matchesFilter =
      filter === "all" ||
      (filter === "in" && v.vendorCheckedIn) ||
      (filter === "pending" && !v.vendorCheckedIn);
    return matchesSearch && matchesFilter;
  });

  const checkedInCount = vendors.filter(v => v.vendorCheckedIn).length;

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-grey-50">
      <Loader className="w-8 h-8 text-grey-400 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Header */}
      <div className="bg-white border-b border-grey-200 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to={`/eo/events/${eventId}`} className="p-1.5 hover:bg-grey-100 rounded-lg transition">
              <ChevronLeft className="w-5 h-5 text-grey-600" />
            </Link>
            <div>
              <h1 className="text-base font-semibold text-grey-900">Vendor Check-In</h1>
              <p className="text-xs text-grey-500">{eventName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-grey-50 border border-grey-200 rounded-xl px-3 py-1.5">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm font-semibold text-grey-900">{checkedInCount}</span>
            <span className="text-sm text-grey-400">/</span>
            <span className="text-sm text-grey-600">{vendors.length}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-2xl mx-auto px-4 pb-3 flex gap-1">
          <button
            onClick={() => setActiveTab("list")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${activeTab === "list" ? "bg-grey-900 text-white" : "text-grey-500 hover:bg-grey-100"}`}
          >
            <Users className="w-3.5 h-3.5" />
            Vendor List
          </button>
          <button
            onClick={() => setActiveTab("scanner")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${activeTab === "scanner" ? "bg-grey-900 text-white" : "text-grey-500 hover:bg-grey-100"}`}
          >
            <QrCode className="w-3.5 h-3.5" />
            Scan QR
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">

        {/* Scan result toast */}
        {scanResult && (
          <div className={`mb-4 p-4 rounded-2xl flex items-center gap-3 ${
            scanResult.status === "success" ? "bg-green-50 border border-green-200" :
            scanResult.status === "already" ? "bg-yellow-50 border border-yellow-200" :
            "bg-red-50 border border-red-200"
          }`}>
            {scanResult.status === "success" && <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />}
            {scanResult.status === "already" && <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />}
            {scanResult.status === "error" && <XCircle className="w-6 h-6 text-red-600 flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              {scanResult.status === "success" && (
                <>
                  <p className="font-semibold text-green-800">Checked in — {scanResult.businessName}</p>
                  {scanResult.boothNumber && (
                    <p className="text-sm text-green-600">Booth {scanResult.boothNumber}</p>
                  )}
                </>
              )}
              {scanResult.status === "already" && (
                <>
                  <p className="font-semibold text-yellow-800">Already checked in — {scanResult.businessName}</p>
                  {scanResult.boothNumber && (
                    <p className="text-sm text-yellow-600">Booth {scanResult.boothNumber}</p>
                  )}
                </>
              )}
              {scanResult.status === "error" && (
                <p className="font-semibold text-red-800">{scanResult.message}</p>
              )}
            </div>
            <button onClick={() => setScanResult(null)} className="p-1 opacity-50 hover:opacity-100 transition flex-shrink-0">
              <XCircle className="w-4 h-4 text-grey-500" />
            </button>
          </div>
        )}

        {/* Scanner tab */}
        {activeTab === "scanner" && (
          <div className="space-y-4">
            {cameraActive ? (
              <div className="bg-black rounded-2xl overflow-hidden relative">
                <video
                  ref={videoRef}
                  className="w-full aspect-square object-cover"
                  playsInline
                  muted
                />
                <canvas ref={canvasRef} className="hidden" />
                {/* Scan overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-56 h-56 border-2 border-white rounded-2xl opacity-60" />
                </div>
                {scanning && (
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                    <div className="bg-black/60 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2">
                      <Loader className="w-3 h-3 animate-spin" />
                      Checking in…
                    </div>
                  </div>
                )}
                <button
                  onClick={stopCamera}
                  className="absolute top-3 right-3 p-2 bg-black/50 hover:bg-black/70 text-white rounded-xl transition"
                >
                  <CameraOff className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="bg-white border border-grey-200 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <QrCode className="w-5 h-5 text-grey-400" />
                  <div>
                    <h3 className="font-medium text-grey-900">Scan Vendor Pass</h3>
                    <p className="text-xs text-grey-500">Use camera or a USB QR scanner</p>
                  </div>
                </div>

                <button
                  onClick={startCamera}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-grey-900 hover:bg-grey-800 text-white rounded-xl text-sm font-medium transition mb-4"
                >
                  <Camera className="w-4 h-4" />
                  Use Camera
                </button>

                {cameraError && (
                  <p className="text-xs text-red-500 mb-3">{cameraError}</p>
                )}

                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-grey-200" />
                  <span className="text-xs text-grey-400">or enter manually</span>
                  <div className="flex-1 h-px bg-grey-200" />
                </div>

                <form onSubmit={handleQrSubmit}>
                  <div className="flex gap-2">
                    <input
                      ref={qrInputRef}
                      type="text"
                      value={qrInput}
                      onChange={e => setQrInput(e.target.value)}
                      placeholder="Paste vendor pass ID…"
                      className="flex-1 px-3.5 py-2.5 border border-grey-300 rounded-xl text-sm focus:ring-2 focus:ring-grey-900 focus:border-transparent font-mono"
                      autoComplete="off"
                    />
                    <button
                      type="submit"
                      disabled={!qrInput.trim() || scanning}
                      className="px-4 py-2.5 bg-grey-900 text-white rounded-xl text-sm font-medium disabled:opacity-50 transition flex items-center gap-2"
                    >
                      {scanning ? <Loader className="w-4 h-4 animate-spin" /> : "Check In"}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {/* Vendor list tab */}
        {activeTab === "list" && (
          <div className="space-y-3">
            {/* Search + filter */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name or booth #..."
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-grey-200 rounded-xl text-sm focus:ring-2 focus:ring-grey-900 focus:border-transparent"
                />
              </div>
              <select
                value={filter}
                onChange={e => setFilter(e.target.value)}
                className="px-3 py-2.5 bg-white border border-grey-200 rounded-xl text-sm text-grey-700 focus:ring-2 focus:ring-grey-900 focus:border-transparent"
              >
                <option value="all">All</option>
                <option value="pending">Not yet in</option>
                <option value="in">Checked in</option>
              </select>
            </div>

            {vendors.length === 0 ? (
              <div className="bg-white border border-grey-200 rounded-2xl p-10 text-center">
                <Store className="w-10 h-10 text-grey-300 mx-auto mb-3" />
                <p className="text-grey-500 text-sm">No confirmed vendors for this event.</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border border-grey-200 rounded-2xl p-8 text-center">
                <p className="text-grey-500 text-sm">No results for "{search}"</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filtered.map(vendor => (
                  <div
                    key={vendor.id}
                    className={`bg-white border rounded-2xl p-4 flex items-center gap-3 ${
                      vendor.vendorCheckedIn ? "border-green-200 bg-green-50" : "border-grey-200"
                    }`}
                  >
                    {/* Avatar */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden ${
                      vendor.vendorCheckedIn ? "bg-green-100" : "bg-grey-100"
                    }`}>
                      {vendor.companyLogoUrl ? (
                        <img src={vendor.companyLogoUrl} alt="" className="w-full h-full object-cover" />
                      ) : vendor.vendorCheckedIn ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <span className="text-sm font-bold text-grey-600">
                          {(vendor.businessName || "?")[0].toUpperCase()}
                        </span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-grey-900 text-sm truncate">{vendor.businessName}</p>
                      <p className="text-xs text-grey-500">{vendor.businessType || vendor.category}</p>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {vendor.boothNumber && (
                        <span className="text-xs font-semibold bg-grey-100 text-grey-700 px-2 py-1 rounded-lg">
                          Booth {vendor.boothNumber}
                        </span>
                      )}
                      {vendor.vendorCheckedIn ? (
                        <span className="text-xs text-green-600 font-medium">Checked in</span>
                      ) : (
                        <button
                          onClick={() => handleCheckIn(JSON.stringify({ applicationId: vendor.id, eventId, type: "vendor-checkin" }))}
                          disabled={scanning}
                          className="px-3 py-1.5 bg-grey-900 text-white text-xs rounded-lg hover:bg-grey-800 transition font-medium disabled:opacity-50"
                        >
                          Check In
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorCheckIn;
